import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  Stack,
  TextField,
} from "@mui/material";
import { FormDivider } from "../BusinessEntityForm/BusinessEntityForm";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import { ValidationResult } from "joi";
import {
  NewTractorInput,
  AssetStatus,
  CustomFieldContext,
} from "../../../graphql/generated";
import AddressInput from "../AddressInput";
import { intersection, isArray, mergeWith, omit, without } from "lodash";
import tractorSchema from "./tractorSchema";
import LocaleProvider from "../../../providers/LocaleProvider";
import formatWeight from "../../../utils/labels/formatWeight";
import { localWeightToKg } from "../../../utils/conversion/weight";
import useConfirmBeforeLeave from "../../../utils/hooks/useConfirmBeforeLeave";
import EnumSelect from "../../common/EnumSelect";
import { useTranslation } from "react-i18next";
import ChipTagsInput from "../../common/ChipTagsInput/ChipTagsInput";
import CustomFieldsFormContainer from "../../extensions/CustomFieldsForm";
import GroupSelectContainer from "../GroupSelect";
import MileageInput from "../../accounting/BillingRuleForm/value-selectors/MileageInput";
import DocumentsUploadModalContainer from "../../common/DocumentsUploadModal";
import { RemoveCircle } from "@mui/icons-material";
import LabeledAddButton from "../../common/LabeledAddButton";
import { useConfirm } from "material-ui-confirm";
import getChangedProperties from "../../../utils/common/getChangedProperties";
import { testIds } from "./TractorForm.testIds";

const ELD_NON_EDITABLE_FIELDS: (keyof NewTractorInput)[] = [
  "serialNumber",
  "status",
];
const ELD_DANGEROUSLY_EDITABLE_FIELDS: (keyof NewTractorInput)[] = [
  "plateNumber",
  "make",
  "model",
  "year",
  "tareWeight",
  "mileage",
  "domicile",
];

type TractorFormProps = {
  initialTractor?: NewTractorInput;
  saving: boolean;
  onSave: (tractor: NewTractorInput) => void;
  hasELDExtensionInstalled?: boolean;
};
type PartialTractor = Partial<NewTractorInput>;
type DeepPartialTractor = {
  [key in keyof PartialTractor]: Partial<PartialTractor[key]>;
};

const TractorForm = ({
  initialTractor,
  saving,
  onSave,
  hasELDExtensionInstalled,
}: TractorFormProps) => {
  const { t } = useTranslation(["assets", "common"]);
  const [validationResult, setValidationResult] =
    useState<ValidationResult<NewTractorInput> | null>(null);
  const [localTractor, setLocalTractor] = useState<Partial<NewTractorInput>>(
    initialTractor || {}
  );
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);

  const getFieldError = (field: string, partialPathMatch = false) =>
    validationResult?.error?.details.find((error) =>
      partialPathMatch
        ? error.path.join(".").startsWith(field)
        : error.path.join(".") === field
    )?.message;

  const onChange = useCallback((changes: DeepPartialTractor) => {
    setLocalTractor((localTractor) =>
      mergeWith({}, localTractor, changes, (objValue, srcValue) => {
        if (isArray(srcValue)) {
          return srcValue;
        }
      })
    );
  }, []);

  const validate = () => {
    const validationResult = tractorSchema.validate(omit(localTractor, "_id"), {
      abortEarly: false,
    });
    setValidationResult(validationResult);
    return !validationResult.error;
  };

  useEffect(() => {
    if (validationResult) {
      validate();
    }
    // We don't want to run everytime validationResult changes
    // otherwise we ill have an infinite update loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTractor]);

  const { cancelConfirm } = useConfirmBeforeLeave(localTractor);

  const isFieldDisabled = useCallback(
    (fieldName: keyof NewTractorInput) => {
      return (
        !!initialTractor?.eldId &&
        ELD_NON_EDITABLE_FIELDS.includes(fieldName) &&
        hasELDExtensionInstalled
      );
    },
    [initialTractor, hasELDExtensionInstalled]
  );

  const confirm = useConfirm();

  const onDangerousChangesSave = async () => {
    try {
      if (!initialTractor || !localTractor) {
        return;
      }
      const changedPropertiesDict = getChangedProperties(
        initialTractor,
        localTractor
      );
      const changedProperties = Object.keys(changedPropertiesDict);

      if (
        intersection(ELD_DANGEROUSLY_EDITABLE_FIELDS, changedProperties).length
      ) {
        await confirm({
          title: t("common:warning", "Warning"),
          description: t(
            "assets:tractor.thirdPartyChangesConfirmation",
            "You are about to edit fields that are managed by a third-party system. Are you sure you want to continue ?"
          ),
          confirmationButtonProps: {
            color: "error",
          },
          confirmationText: t("common:yes", "Yes"),
          cancellationText: t("common:error.noCancel", "No, Cancel"),
        });
      }
      onSave(localTractor as NewTractorInput);
    } catch (error) {
      // Submission is cancelled
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {initialTractor && initialTractor.eldId && (
          <Grid item sm={12}>
            <Alert severity="info">
              {t(
                hasELDExtensionInstalled
                  ? "assets:tractor.thirdPartyWarning"
                  : "assets:tractor.thirdPartyWarning_uninstalledELD",
                `This tractor is managed by a third-party service. ${
                  hasELDExtensionInstalled
                    ? "Some fields will not be editable."
                    : "ELD extension is not installed."
                }`
              )}
            </Alert>
          </Grid>
        )}
        <Grid item sm={6}>
          <FormDivider
            variant="fullWidth"
            text={t("assets:tractor.details", "Tractor Details")}
          />
          <Grid container spacing={3}>
            <Grid item sm={12}>
              <TextField
                label={t("assets:code", "Asset Code")}
                name="assetCode"
                required
                fullWidth
                value={localTractor?.serialNumber || ""}
                error={!!getFieldError("serialNumber")}
                helperText={getFieldError("serialNumber")}
                onChange={(event) => {
                  onChange({ serialNumber: event.target.value });
                }}
                disabled={isFieldDisabled("serialNumber")}
              inputProps={{ "data-testid": testIds.tractorFormAssetCode } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label="VIN#"
                name="vinNumber"
                fullWidth
                value={localTractor?.plateNumber || ""}
                error={!!getFieldError("plateNumber")}
                helperText={getFieldError("plateNumber")}
                onChange={(event) => {
                  onChange({ plateNumber: event.target.value });
                }}
              inputProps={{ "data-testid": testIds.tractorFormVinNumber } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label={t("assets:licenseNumber", "License Number")}
                name="licenseNumber"
                required
                fullWidth
                value={localTractor?.licenseNumber || ""}
                error={!!getFieldError("licenseNumber")}
                helperText={getFieldError("licenseNumber")}
                onChange={(event) => {
                  onChange({ licenseNumber: event.target.value });
                }}
              inputProps={{ "data-testid": testIds.tractorFormLicenseNumber } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label={t("assets:tractor.make", "Make")}
                name="make"
                fullWidth
                value={localTractor?.make || ""}
                error={!!getFieldError("make")}
                helperText={getFieldError("make")}
                onChange={(event) => {
                  onChange({ make: event.target.value });
                }}
              inputProps={{ "data-testid": testIds.tractorFormMake } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label={t("assets:tractor.model", "Model")}
                name="model"
                fullWidth
                value={localTractor?.model || ""}
                error={!!getFieldError("model")}
                helperText={getFieldError("model")}
                onChange={(event) => {
                  onChange({ model: event.target.value });
                }}
              inputProps={{ "data-testid": testIds.tractorFormModel } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label={t("assets:tractor.year", "Year")}
                name="year"
                fullWidth
                value={localTractor?.year || ""}
                error={!!getFieldError("year")}
                helperText={getFieldError("year")}
                onChange={(event) => {
                  onChange({ year: Number(event.target.value) });
                }}
              inputProps={{ "data-testid": testIds.tractorFormYear } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label={t("assets:tractor.externalId", "External ID")}
                name="externalId"
                fullWidth
                value={localTractor?.externalId || ""}
                error={!!getFieldError("externalId")}
                helperText={getFieldError("externalId")}
                onChange={(event) => {
                  onChange({ externalId: event.target.value });
                }}
              inputProps={{ "data-testid": testIds.tractorFormExternalId } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                label={t("assets:tractor.tareWeightWithUnit", {
                  unit: LocaleProvider.getWeightUnit(),
                  defaultValue: `Tare Weight (${LocaleProvider.getWeightUnit()})`,
                })}
                name="tareWeight"
                fullWidth
                value={formatWeight(
                  Number(localTractor?.tareWeight || 0),
                  false
                )}
                error={!!getFieldError("tareWeight")}
                helperText={getFieldError("tareWeight")}
                onChange={(event) => {
                  onChange({
                    tareWeight: localWeightToKg(
                      !isNaN(Number.parseFloat(event.target.value))
                        ? Number.parseFloat(event.target.value)
                        : 0
                    ),
                  });
                }}
              inputProps={{ "data-testid": testIds.tractorFormStatus } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item sm={12}>
              <MileageInput
                value={localTractor?.mileage ? localTractor.mileage : ""}
                onChange={(value) => {
                  onChange({
                    mileage: Number(value),
                  });
                }}
              />
            </Grid>

            <Grid item sm={12}>
              <EnumSelect data-testid="tractor-form-status"
                label={t("assets:status", "Status")}
                name="status"
                fullWidth
                enumObject={AssetStatus}
                optionLabel={(status) => t(`common:statusTypes.${status}`)}
                error={!!getFieldError("status")}
                helperText={getFieldError("status")}
                value={localTractor?.status || AssetStatus.Active}
                onChange={(event, status) => {
                  if (!status) {
                    return;
                  }
                  onChange({
                    status,
                  });
                }}
                disabled={isFieldDisabled("status")}
              />
            </Grid>

            <Grid item sm={12}>
              <ChipTagsInput
                value={localTractor?.tags || []}
                onChange={(tags) => {
                  onChange({ tags });
                }}
              />
            </Grid>
            <Grid item sm={12}>
              <GroupSelectContainer data-testid="tractor-form-group-ids-select"
                value={localTractor?.groupIds || []}
                onChange={(groupIds) => {
                  onChange({ groupIds });
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item sm={6}>
          <FormDivider
            variant="fullWidth"
            text={t("assets:domicile", "Domicile")}
          />
          <ErrorMessage message={getFieldError("domicile")} />

          <AddressInput
            value={localTractor?.domicile || null}
            onChange={(domicile) => {
              onChange({ domicile });
            }}
            errors={{
              label: getFieldError("domicile.label"),
              coordinates: getFieldError("domicile.coordinates"),
              line1: getFieldError("domicile.line1"),
              line2: getFieldError("domicile.line2"),
              postalCode: getFieldError("domicile.postalCode"),
              state: getFieldError("domicile.state"),
              city: getFieldError("domicile.city"),
              country: getFieldError("domicile.country"),
            }}
          />
        </Grid>

        <Grid item sm={6}>
          <FormDivider variant="fullWidth" text={t("common:customFields")} />

          <CustomFieldsFormContainer
            context={CustomFieldContext.Tractor}
            customFields={localTractor?.customFields || []}
            onChange={(customFields) => {
              onChange({ customFields });
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormDivider
            variant="fullWidth"
            text={t("common:documents.many")}
            sx={{
              textTransform: "capitalize",
            }}
          />

          {localTractor?.documents?.map((document) => {
            return (
              <Stack direction="row" alignItems="flex-start">
                <Box flex={1}>
                  <Link data-testid={`${testIds.tractorFormOpenDocument}-${document.url}`} href={document.url} component="a" target="_blank">
                    {document.name}
                  </Link>
                </Box>

                <IconButton data-testid={`${testIds.tractorFormRemoveDocument}-${document.url}`}
                  onClick={() =>
                    onChange({
                      documents: without(localTractor.documents, document),
                    })
                  }
                >
                  <RemoveCircle />
                </IconButton>
              </Stack>
            );
          })}

          <LabeledAddButton data-testid="tractor-form-add-document"
            label={t("common:documents.add", "Add Document")}
            onClick={() => setDocumentUploadModalOpen(true)}
          />

          <DocumentsUploadModalContainer
            isOpen={documentUploadModalOpen}
            onSubmit={(documents) => {
              onChange({
                documents: (localTractor?.documents || []).concat(documents),
              });
              setDocumentUploadModalOpen(false);
            }}
            onCancel={() => setDocumentUploadModalOpen(false)}
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          pt: 3,
        }}
      >
        <Button data-testid={testIds.tractorFormSave}
          variant="contained"
          disabled={saving || !!validationResult?.error}
          size="large"
          onClick={() => {
            if (validate()) {
              cancelConfirm();
              if (!initialTractor || !localTractor.eldId) {
                onSave(localTractor as NewTractorInput);
              } else {
                onDangerousChangesSave();
              }
            }
          }}
          id="saveTractorButton"
        >
          {t("common:save", "Save")}
        </Button>
        <Box sx={{ mr: 1 }}>
          <ErrorMessage message={validationResult?.error?.message || null} />
        </Box>
      </Box>
    </Box>
  );
};

export default TractorForm;
